import { Cat, KINDS } from '../../../gameplay/registry';
import { TICK_RATE } from '../../../gameplay/stats';
import { FX_ONE } from '../fixed';
import { Order, UState, type World } from '../world';
import { approach, setDestination, stopMoving } from './movement';
import { orderBuild } from './construction';
import { findNearbyResource, gatherableBy, orderGather, resourceOf } from './economy';

/** How close a unit must come to a building's footprint to go in. */
const BUILDING_REACH = FX_ONE;
/** How close a unit on the shore must come to a ship's hull to board. */
const SHIP_REACH = 2 * FX_ONE;
/** A transport lands its cargo once this close to where it was sent (or when it can sail no closer). */
const LAND_RANGE = 4 * FX_ONE;

/** Whether unit s may be ordered into building or ship c (room is checked on arrival). */
export function mayEnter(w: World, s: number, c: number): boolean {
  if (c < 0 || c === s || !w.alive[c] || w.owner[c] !== w.owner[s] || w.capacityOf(c) === 0) return false;
  if (w.cat[c] === Cat.Building ? !w.built[c] : w.cat[c] !== Cat.Unit) return false;
  return w.placesFor(s, c) > 0;
}

/** Send a transport to land its cargo near (x, y): at once, if it is already close enough. */
export function orderUnload(w: World, s: number, x: number, y: number): void {
  w.order[s] = Order.Unload;
  w.goalX[s] = x;
  w.goalY[s] = y;
  // Where the last leg began: a long voyage is sailed in legs (path searches are limited).
  w.homeX[s] = w.x[s];
  w.homeY[s] = w.y[s];
  w.state[s] = UState.Walking;
  const dx = x - w.x[s];
  const dy = y - w.y[s];
  if (dx * dx + dy * dy > LAND_RANGE * LAND_RANGE) setDestination(w, s, x, y);
  else stopMoving(w, s);
}

/**
 * Back to Work: a unit just out of a building takes up what it was doing when it was sent in.
 * Gatherers go back to the same resource (or the nearest of its kind there, if it ran out),
 * builders to the same building if it still needs work; everyone else walks back to where they were.
 */
export function resumeWork(w: World, s: number): void {
  const ord = w.prevOrder[s];
  const t = w.resolve(w.prevTarget[s]);
  w.prevOrder[s] = Order.None;
  w.prevTarget[s] = -1;
  if (ord === Order.Gather) {
    let r = t >= 0 && resourceOf(w, t) >= 0 && gatherableBy(w, s, t) ? t : -1;
    const lk = w.lastResKind[s];
    if (r < 0 && lk >= 0) {
      const res = KINDS[lk].resource?.res ?? (KINDS[lk].building?.foodAmount !== undefined ? 0 : -1);
      if (res >= 0) r = findNearbyResource(w, s, res, w.prevX[s], w.prevY[s], lk);
    }
    if (r >= 0) {
      orderGather(w, s, r);
      return;
    }
  } else if ((ord === Order.Build || ord === Order.Repair) && t >= 0 && w.cat[t] === Cat.Building && w.owner[t] === w.owner[s] && (!w.built[t] || w.hp[t] < w.maxHp[t])) {
    orderBuild(w, s, t);
    if (w.built[t]) w.order[s] = Order.Repair;
    return;
  }
  const dx = w.prevX[s] - w.x[s];
  const dy = w.prevY[s] - w.y[s];
  if (dx * dx + dy * dy <= FX_ONE * FX_ONE) return;
  w.order[s] = Order.Move;
  w.state[s] = UState.Walking;
  setDestination(w, s, w.prevX[s], w.prevY[s]);
}

function landCargo(w: World, s: number): boolean {
  const gx = w.goalX[s];
  const gy = w.goalY[s];
  const out = w.releaseCargo(s, gx, gy);
  if (!out.length) return false;
  for (const u of out) {
    const dx = gx - w.x[u];
    const dy = gy - w.y[u];
    if (dx * dx + dy * dy <= FX_ONE * FX_ONE || w.pathfinder.isBlockedTile(gx >> 10, gy >> 10)) continue;
    w.order[u] = Order.Move;
    w.state[u] = UState.Walking;
    setDestination(w, u, gx, gy);
  }
  return true;
}

/**
 * Garrisons and transports (rules v8). Units ordered into a building or ship walk to it and go in
 * while there is room, a villager handing over what it carries if the building takes it. Units
 * inside are off the map: they move with their ship and slowly heal inside a building (one hit
 * point a second). A transport sent to unload sails toward the spot and lands everyone on the
 * nearest shore once close, or as close as it can get.
 */
export function updateGarrison(w: World): void {
  const n = w.highSlot;
  const heal = w.tick % TICK_RATE === 0;
  for (let s = 0; s < n; s++) {
    if (!w.alive[s]) continue;
    if (w.cat[s] === Cat.Held) {
      const c = w.resolve(w.inside[s]);
      if (c < 0) continue;
      w.x[s] = w.px[s] = w.x[c];
      w.y[s] = w.py[s] = w.y[c];
      if (heal && w.cat[c] === Cat.Building && w.hp[s] < w.maxHp[s]) w.hp[s]++;
      continue;
    }
    if (w.cat[s] !== Cat.Unit) continue;
    if (w.order[s] === Order.Unload) {
      if (!w.cargo[s]) {
        w.order[s] = Order.None;
        w.state[s] = UState.Idle;
        continue;
      }
      const dx = w.goalX[s] - w.x[s];
      const dy = w.goalY[s] - w.y[s];
      const close = dx * dx + dy * dy <= LAND_RANGE * LAND_RANGE;
      if (!close && !w.moving[s]) {
        // Stopped short of the goal but got somewhere on this leg: sail on (long voyages go in legs,
        // as path searches are limited). No progress means this is as close as it can get.
        const hx = w.x[s] - w.homeX[s];
        const hy = w.y[s] - w.homeY[s];
        if (hx * hx + hy * hy > 4 * FX_ONE * FX_ONE) {
          w.homeX[s] = w.x[s];
          w.homeY[s] = w.y[s];
          setDestination(w, s, w.goalX[s], w.goalY[s]);
          continue;
        }
      }
      if ((close || !w.moving[s]) && landCargo(w, s)) {
        stopMoving(w, s);
        w.order[s] = Order.None;
        w.state[s] = UState.Idle;
      } else if (!w.moving[s]) {
        w.order[s] = Order.None;
        w.state[s] = UState.Idle;
        w.events.push({ e: 'notice', owner: w.owner[s], text: 'No shore to land on there.' });
      }
      continue;
    }
    if (w.order[s] !== Order.Garrison) continue;
    const c = w.resolve(w.target[s]);
    if (!mayEnter(w, s, c)) {
      w.order[s] = Order.None;
      w.state[s] = UState.Idle;
      w.target[s] = -1;
      stopMoving(w, s);
      continue;
    }
    const ship = w.cat[c] === Cat.Unit;
    if (w.edgeDist(c, w.x[s], w.y[s]) > (ship ? SHIP_REACH : BUILDING_REACH)) {
      w.state[s] = UState.Walking;
      // A ship may be moving: head for where it is now, every second.
      if (ship) {
        if (!w.moving[s] || (w.tick + s) % TICK_RATE === 0) setDestination(w, s, w.x[c], w.y[c]);
      } else if (!w.moving[s]) approach(w, s, c);
      continue;
    }
    const places = w.placesFor(s, c);
    if (w.cargo[c] + places > w.capacityOf(c)) {
      w.order[s] = Order.None;
      w.state[s] = UState.Idle;
      w.target[s] = -1;
      stopMoving(w, s);
      continue;
    }
    // A villager hands in what it carries where the building accepts it.
    const drop = KINDS[w.kind[c]].building?.dropoff;
    if (w.carryAmt[s] > 0 && drop && drop.includes(w.carryRes[s])) {
      const p = w.players[w.owner[s]];
      p.res[w.carryRes[s]] += w.carryAmt[s];
      p.gathered[w.carryRes[s]] += w.carryAmt[s];
      w.carryAmt[s] = 0;
    }
    w.hold(s, c);
  }
}
